import api from './api';

// Helper: trigger a file download from an /export endpoint.
// Bypasses the JSON-unwrapping interceptor by using fetch directly with the auth token.
async function downloadXlsx(path, params = {}, filename = "report.xlsx") {
  const token = localStorage.getItem('auth_token');
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.append(k, v); });
  const url = `${api.defaults.baseURL}${path}${qs.toString() ? `?${qs}` : ''}`;
  const resp = await fetch(url, {
    headers: { Authorization: token ? `Bearer ${token}` : '' },
  });
  if (!resp.ok) {
    throw new Error(`Export failed: ${resp.status} ${resp.statusText}`);
  }
  const blob = await resp.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}

export const detailedReportService = {
  // Profit & Loss by Project
  pnlByProject: (params = {}) => api.get('/reports/detailed/profit-loss-by-project', { params }),
  pnlByProjectExport: (params = {}) => downloadXlsx('/reports/detailed/profit-loss-by-project/export', params, 'Profit_and_Loss_by_Project.xlsx'),

  // A/R Aging
  arAging: () => api.get('/reports/detailed/ar-aging'),
  arAgingExport: () => downloadXlsx('/reports/detailed/ar-aging/export', {}, 'AR_Aging.xlsx'),

  // Revenue Report
  revenue: (params = {}) => api.get('/reports/detailed/revenue', { params }),
  revenueExport: (params = {}) => downloadXlsx('/reports/detailed/revenue/export', params, 'Revenue_Report.xlsx'),

  // Vendor Spend
  vendorSpend: (params = {}) => api.get('/reports/detailed/vendor-spend', { params }),
  vendorSpendExport: (params = {}) => downloadXlsx('/reports/detailed/vendor-spend/export', params, 'Vendor_Spend.xlsx'),

  // Lead Source ROI
  leadSourceROI: (params = {}) => api.get('/reports/detailed/lead-source-roi', { params }),
  leadSourceROIExport: (params = {}) => downloadXlsx('/reports/detailed/lead-source-roi/export', params, 'Lead_Source_ROI.xlsx'),

  // Expected Revenue (date_field: "start_date" or "end_date")
  expectedRevenue: (params = {}) => api.get('/reports/detailed/expected-revenue', { params }),
  expectedRevenueExport: (params = {}) => {
    const fname = params.date_field === 'end_date' ? 'Expected_Revenue_by_End_Date.xlsx' : 'Expected_Revenue_by_Start_Date.xlsx';
    return downloadXlsx('/reports/detailed/expected-revenue/export', params, fname);
  },

  // Change Orders
  changeOrders: (params = {}) => api.get('/reports/detailed/change-orders', { params }),
  changeOrdersExport: (params = {}) => downloadXlsx('/reports/detailed/change-orders/export', params, 'Change_Orders.xlsx'),

  // Sales Pipeline
  salesPipeline: () => api.get('/reports/detailed/sales-pipeline'),
  salesPipelineExport: () => downloadXlsx('/reports/detailed/sales-pipeline/export', {}, 'Sales_Pipeline.xlsx'),

  // Customer LTV
  customerLTV: () => api.get('/reports/detailed/customer-ltv'),
  customerLTVExport: () => downloadXlsx('/reports/detailed/customer-ltv/export', {}, 'Customer_LTV.xlsx'),

  // Project Duration
  projectDuration: () => api.get('/reports/detailed/project-duration'),
  projectDurationExport: () => downloadXlsx('/reports/detailed/project-duration/export', {}, 'Project_Duration.xlsx'),

  // Callbacks Analysis
  callbacksAnalysis: (params = {}) => api.get('/reports/detailed/callbacks-analysis', { params }),
  callbacksAnalysisExport: (params = {}) => downloadXlsx('/reports/detailed/callbacks-analysis/export', params, 'Callbacks_Analysis.xlsx'),

  // Customer Acquisition
  customerAcquisition: (params = {}) => api.get('/reports/detailed/customer-acquisition', { params }),
  customerAcquisitionExport: (params = {}) => downloadXlsx('/reports/detailed/customer-acquisition/export', params, 'Customer_Acquisition.xlsx'),

  // Conversion Funnel
  conversionFunnel: (params = {}) => api.get('/reports/detailed/conversion-funnel', { params }),
  conversionFunnelExport: (params = {}) => downloadXlsx('/reports/detailed/conversion-funnel/export', params, 'Conversion_Funnel.xlsx'),

  // Crew Utilization
  crewUtilization: (params = {}) => api.get('/reports/detailed/crew-utilization', { params }),
  crewUtilizationExport: (params = {}) => downloadXlsx('/reports/detailed/crew-utilization/export', params, 'Crew_Utilization.xlsx'),

  // User Activity
  userActivity: (params = {}) => api.get('/reports/detailed/user-activity', { params }),
  userActivityExport: (params = {}) => downloadXlsx('/reports/detailed/user-activity/export', params, 'User_Activity.xlsx'),

  // Geographic
  jobsByLocation: (params = {}) => api.get('/reports/detailed/jobs-by-location', { params }),
  jobsByLocationExport: (params = {}) => downloadXlsx('/reports/detailed/jobs-by-location/export', params, 'Jobs_by_Location.xlsx'),
  proximitySearch: (params = {}) => api.get('/reports/detailed/proximity-search', { params }),
  proximitySearchExport: (params = {}) => downloadXlsx('/reports/detailed/proximity-search/export', params, `Proximity_${params.entity || 'project'}.xlsx`),
  mapData: (params = {}) => api.get('/reports/detailed/map-data', { params }),
  geocodingStatus: () => api.get('/reports/detailed/geocoding-status'),
  clearFailedGeocodes: () => api.post('/reports/detailed/clear-failed-geocodes'),
  statusOptions: (entity) => api.get('/reports/detailed/status-options', { params: { entity } }),

  paymentAudit: () => api.get('/reports/detailed/payment-audit'),
  paymentAuditExport: () => downloadXlsx('/reports/detailed/payment-audit/export', {}, 'Payment_Audit.xlsx'),

  // Edit/move a cost line item (shared endpoint with the Costs & Wages report)
  editCostItem: (data) => api.put('/reports/costs-and-wages/item', data),
};
